import * as moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../components/custom-table/custom-table";
import DownloadFilesModal from "../../../components/modals/downloadFilesModal/downloadFilesModal";
import ReassignCaseModal, {
  ReassignCasePayload,
} from "../../../components/modals/reassignCaseModal/reassignCaseModal";
import SendToAppealModal from "../../../components/modals/sendToAppealModal/sendToAppealModal";
import AppealDetailsModal from "../../../components/modals/appealDetailsModal/appealDetailsModal";

import LetterUploadDoc from "../../../components/modals/LetterUploadDoc/letterUploadDoc";
import SftpDocModal from "../../../components/modals/sftpDocModal/sftpDocModal";
import SftpPushPendingRecords from "../../../components/modals/SftpPushPendingRecords/sftpPushPendingRecords";
import SftpRerun from "../../../components/modals/SftpRerun/sftpRerun";
import SftpSummary from "../../../components/modals/SftpSummary/sftpSummary";
import StatFileUploadModal from "../../../components/modals/StatFileUpload/statFileUploadModal";
import TextNoteDoc from "../../../components/modals/TextNoteDoc/textNoteDoc";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { useModalOpener } from "../../../hooks/useModalOpener";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { handleOpenSubmissionSummary } from "../actionHandlers/openSubmissionSummaryHandler";
import { handleReassignCaseAssignWithToast } from "../actionHandlers/handleReassignCaseAssign";
import { handleSaveSftpRerunAction } from "../actionHandlers/rerunModalSave";
import {
  handleDownloadFilesClick,
  handleDownloadFilesModalClick,
  handleSendToSftpClick,
  handleTextNoteButtonClick,
  handleTrackStatusClick,
} from "../actionHandlers/sendToSftpHandler";
import { handleSummaryStatsClick } from "../actionHandlers/sftpStatsHandler";
import { handleSummaryClick } from "../actionHandlers/sftpSummary";
import { handleStatFileUploadClick } from "../actionHandlers/statFileUploadHandler";
import {
  canOpenAppealDetailsModal,
  canOpenSendToAppealModal,
} from "../../pharmaOncologySubmission/actionHandlers/onSendToAppealClick";
import { useDenialLetterUploadScreenshot } from "../hooks/useDenialLetterUploadScreenshot";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { usePharmaStpFileTableData } from "../hooks/usePharmaStpFileTableData";
import { useTextNoteScreenshot } from "../hooks/useTextNoteScreenshot";
import { useTextNotesSignedScreenshot } from "../hooks/useTextNotesSignedScreenshot";
import {
  generateTableDataForPharmaStpFileTable,
  PharmaStpFileTableHeader,
} from "../table/pharmaStpFileTableData";
import { shouldOpenCmmModal } from "../table/utils/shouldOpenCmmModal";
import { shouldOpenLetterUploadModal } from "../table/utils/shouldOpenLetterUploadModal";
import { shouldOpenModal } from "../table/utils/shouldOpenModal";
import {
  buildPharmaStpFileToolbarItems,
} from "../utils/buttonDropdownConfig";
import { createAllFilterSections } from "../utils/createFilterSections";

const PharmaStpFileTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const organizationType = getOrgIdForFetchExternalWorklist();

  useModalOpener();

  const getLast14DaysDateRange = () => {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const endDate = `${year}${month}${day}`;
    const startDateObj = new Date(now);
    startDateObj.setDate(now.getDate() - 13);
    const startYear = startDateObj.getFullYear();
    const startMonth = String(startDateObj.getMonth() + 1).padStart(2, "0");
    const startDay = String(startDateObj.getDate()).padStart(2, "0");
    const startDate = `${startYear}${startMonth}${startDay}`;

    return { startDate, endDate };
  };

  const { startDate, endDate } = getLast14DaysDateRange();
  const pageSize = 120;

  const [filterData, setFilterData] = useState<FilterSection[]>([]);
  const [allDataForFilters, setAllDataForFilters] = useState<any[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { pharmaStpFileState, setPharmaStpFileState, tableData, setTableData } =
    usePharmaStpFileTableData();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setPharmaStpFileState,
      setTableData,
      startDate,
      endDate,
      pageSize,
    );

  const { paginationResetCount, setSearchTerm, searchTerm, validFilters } =
    useFilterAndSearch(
      initialDataFetchingExecuted,
      setTableData,
      setPharmaStpFileState,
      startDate,
      endDate,
      pageSize,
    );

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    pharmaStpFileState,
    setPharmaStpFileState,
    setTableData,
    startDate,
    endDate,
    pageSize,
  );

  const [allStpFileOrdersTableData, setAllStpFileOrdersTableData] = useState<
    any[]
  >([]);
  const [selectedIdentifier, setSelectedIdentifier] = useState<string | null>(
    null,
  );
  const [selectedLetterUploadIdentifier, setSelectedLetterUploadIdentifier] =
    useState<string | null>(null);
  const [selectedRerunIdentifier, setSelectedRerunIdentifier] = useState<
    string | null
  >(null);
  const [isRerunning, setIsRerunning] = useState(false);
  const [sendToAppealRow, setSendToAppealRow] =
    useState<OncologySubmissionTableDataModel | null>(null);
  const [appealDetailsIdentifier, setAppealDetailsIdentifier] = useState<
    string | null
  >(null);

  const {
    screenshotUrl,
    formattedTime: screenshotFormattedTime,
    loading: screenshotLoading,
    error: screenshotError,
  } = useTextNoteScreenshot(selectedIdentifier);

  const {
    signedScreenshotUrl,
    loading: signedScreenshotLoading,
    error: signedScreenshotError,
  } = useTextNotesSignedScreenshot(selectedIdentifier);

  const [letterUploadTimestamp, setLetterUploadTimestamp] =
    useState<string>("");

  const {
    screenshotUrl: letterUploadScreenshotUrl,
    loading: letterUploadLoading,
    error: letterUploadError,
  } = useDenialLetterUploadScreenshot(selectedLetterUploadIdentifier);

  const handleStpDetailsPopupMarkSuccess = useCallback(() => {
    setTableDataForContext(TableNames.PHARMA_STP_FILE_ORDERS, {
      shouldRefetch: true,
    });
  }, [setTableDataForContext]);

  useEffect(() => {
    const formattedTableData = generateTableDataForPharmaStpFileTable(
      tableData,
      { onStpDetailsPopupMarkSuccess: handleStpDetailsPopupMarkSuccess },
    );
    setAllStpFileOrdersTableData(formattedTableData);

    // Accumulate data for filter options in two scenarios:
    // 1. First load (even with filters, to populate initial filter options)
    // 2. When no filters are applied (to get complete dataset)
    if (
      tableData &&
      tableData.length > 0 &&
      (isFirstLoad || !validFilters || validFilters.length === 0)
    ) {
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }

      setAllDataForFilters((prevData) => {
        // Combine previous and new data
        const combinedData = [...prevData, ...tableData];

        // Remove duplicates based on identifier
        const uniqueData = combinedData.reduce((acc, item) => {
          if (!acc.some((x: any) => x.identifier === item.identifier)) {
            acc.push(item);
          }
          return acc;
        }, [] as any[]);

        return uniqueData;
      });
    }
  }, [tableData, validFilters, isFirstLoad, handleStpDetailsPopupMarkSuccess]);

  const handleSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  const handleRefresh = () => {
    setInitialDataFetchingExecuted(false);
    setAllDataForFilters([]);
    setIsFirstLoad(true);
  };

  const handleViewCmmClick = (rowData: any) => {
    if (shouldOpenCmmModal(rowData.cmmStatus)) {
      dispatch(
        setOpenedModalId({
          id: ModalId.SFTP_DOC_MODAL,
          metaData: {
            selectedRowData: rowData,
          },
        }),
      );
    }
  };

  const handleTextNoteClick = (rowData: any) => {
    const identifier = rowData?.identifier;

    if (identifier && shouldOpenModal(rowData.textNoteStatus)) {
      setSelectedIdentifier(identifier);
      dispatch(
        setOpenedModalId({
          id: ModalId.TEXT_NOTE_DOC,
          metaData: {
            selectedRowData: rowData,
            identifier: identifier,
          },
        }),
      );
    } else {
      console.error("No identifier found in row data");
    }
  };

  const handleLetterUploadClick = (rowData: any) => {
    const identifier = rowData?.identifier;

    if (identifier && shouldOpenLetterUploadModal(rowData.letterUploadStatus)) {
      setSelectedLetterUploadIdentifier(identifier);
      const updatedAt = rowData?.letterUploadUpdatedAt;
      setLetterUploadTimestamp(
        updatedAt
          ? moment
              .utc(updatedAt)
              .add(5, "hours")
              .add(30, "minutes")
              .format("MMM DD, YYYY h:mm A")
          : "",
      );
      dispatch(
        setOpenedModalId({
          id: ModalId.LETTER_UPLOAD_DOC,
          metaData: {
            selectedRowData: rowData,
            identifier: identifier,
          },
        }),
      );
    }
  };

  const handleRowClick = (rowData: any) => {};

  const handleCloseSftpDocModal = () => {
    dispatch(closeModal());
  };

  const handleCloseTextNoteDocModal = () => {
    dispatch(closeModal());
    setSelectedIdentifier(null);
  };

  const handleSaveTextNoteDoc = () => {
    // Add save logic here when needed
    dispatch(closeModal());
    setSelectedIdentifier(null);
  };

  const handleCloseLetterUploadDocModal = () => {
    dispatch(closeModal());
    setSelectedLetterUploadIdentifier(null);
    setLetterUploadTimestamp("");
  };

  const handleSendToSftp = () => {
    handleSendToSftpClick(dispatch);
  };

  const handleSummaryStats = () => {
    handleSummaryStatsClick(dispatch);
  };

  const handleSummary = () => {
    handleSummaryClick(dispatch);
  };

  const handleTrackStatus = () => {
    handleTrackStatusClick(dispatch);
  };

  const handleTextNote = () => {
    handleTextNoteButtonClick(dispatch);
  };

  const handleDownloadFiles = () => {
    handleDownloadFilesClick(dispatch);
  };

  const handleStatFileUpload = () => {
    handleStatFileUploadClick(dispatch);
  };

  const handleRunDeliveryPipelineClick = () => {
    dispatch(setOpenedModalId({ id: ModalId.RUN_DELIVERY_PIPELINE_MODAL }));
  };

  const handleReassignCaseClick = () => {
    dispatch(setOpenedModalId({ id: ModalId.REASSIGN_CASE_MODAL }));
  };

  const handleReassignCaseAssign = async (
    payload: ReassignCasePayload,
  ): Promise<void> => {
    await handleReassignCaseAssignWithToast(payload, () => {
      setTableDataForContext(TableNames.PHARMA_STP_FILE_ORDERS, {
        shouldRefetch: true,
      });
    });
  };

  const handleAddKeyButtonClick = (rowData: any) => {
    // Pass raw row (PharmaStpFileModel) so modal can show MRN, Drug, Key and prefill fields
    const rawData =
      tableData?.find((r: any) => r.identifier === rowData.identifier) ??
      rowData;
    handleOpenSubmissionSummary(dispatch, rawData);
  };

  /**
   * Send to Appeal column: View opens Appeal Details; Send opens generate/send modal.
   *
   * @param rowData - Formatted table row including `sendToAppeal` badge state
   */
  const handleSendToAppealBadgeClick = useCallback(
    (rowData: OncologySubmissionTableDataModel) => {
      if (canOpenAppealDetailsModal(rowData)) {
        const identifier = String(rowData.identifier ?? "").trim();
        if (identifier) {
          setAppealDetailsIdentifier(identifier);
        }
        return;
      }

      if (canOpenSendToAppealModal(rowData)) {
        setSendToAppealRow(rowData);
      }
    },
    [],
  );

  /**
   * Closes the Appeal Details modal opened from Send to Appeal View.
   */
  const handleCloseAppealDetailsModal = useCallback(() => {
    setAppealDetailsIdentifier(null);
  }, []);

  const handleCloseSendToAppealModal = useCallback(() => {
    setSendToAppealRow(null);
  }, []);

  const handleSendToAppealSuccess = useCallback(() => {
    setInitialDataFetchingExecuted(false);
  }, [setInitialDataFetchingExecuted]);

  useEffect(() => {
    if (allDataForFilters && allDataForFilters.length > 0) {
      const filters = createAllFilterSections(allDataForFilters);
      setFilterData(filters);
    }
  }, [allDataForFilters]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  // Watch for shouldRefetch flag from context
  const tableContextData = getTableDataFromContext(
    TableNames.PHARMA_STP_FILE_ORDERS,
  );

  useEffect(() => {
    if (tableContextData?.shouldRefetch) {
      // Trigger refetch
      setInitialDataFetchingExecuted(false);
      // Reset the flag
      setTableDataForContext(TableNames.PHARMA_STP_FILE_ORDERS, {
        shouldRefetch: false,
      });
    }
  }, [
    tableContextData?.shouldRefetch,
    setInitialDataFetchingExecuted,
    setTableDataForContext,
  ]);

  if (pharmaStpFileState.error) {
    console.error("Error fetching STP file data:", pharmaStpFileState.error);
  }

  const toolbarItems = buildPharmaStpFileToolbarItems({
    handlers: {
      handleDownloadFiles,
      handleSummary,
      handleDownloadFilesModal: () => handleDownloadFilesModalClick(dispatch),
      handleTrackStatus,
      handleTextNote,
      handleSendToSftp,
      handleSummaryStats,
    },
    onReassignCase: handleReassignCaseClick,
    onStatFileUpload: handleStatFileUpload,
    onRunDeliveryPipeline: handleRunDeliveryPipelineClick,
    isLoading: pharmaStpFileState.loading,
    organizationType,
  });

  const handleThreeDotsOptionClick = (optionId: string, rowId: string) => {
    if (optionId === "rerun_status") {
      setSelectedRerunIdentifier(rowId);
      dispatch(setOpenedModalId({ id: ModalId.SFTP_RERUN_MODAL }));
    }
  };
  const handleCloseSftpRerunModal = () => {
    dispatch(closeModal());
    setSelectedRerunIdentifier(null);
    setIsRerunning(false);
  };
  const handleSaveSftpRerun = () => {
    handleSaveSftpRerunAction({
      dispatch,
      selectedRerunIdentifier,
      setIsRerunning,
      setSelectedRerunIdentifier,
    });
  };

  const handleCloseSftpSummaryModal = () => {
    dispatch(closeModal());
  };

  return (
    <>
      <CustomTable
        tableHeaders={PharmaStpFileTableHeader}
        tableData={allStpFileOrdersTableData}
        itemsPerPage={10}
        pagesPerView={4}
        count={allStpFileOrdersTableData.length}
        totalCount={pharmaStpFileState.data?.total_count ?? 0}
        isFetching={pharmaStpFileState.loading}
        tableName={TableNames.PHARMA_STP_FILE_ORDERS}
        searchingText={handleSearch}
        onViewCmmClick={(data) => handleViewCmmClick(data)}
        onTextNoteClick={(data) => handleTextNoteClick(data)}
        onLetterUploadClick={(data) => handleLetterUploadClick(data)}
        onRefreshButton={handleRefresh}
        onRowClick={(data) => handleRowClick(data)}
        isDisabled={pharmaStpFileState.loading}
        showRefreshButton={true}
        showOtherButton={false}
        orderedToolbarMeta={toolbarItems}
        showFilterButton={true}
        filterSectionsData={filterData}
        onFilterApplyClicked={handleFilterApply}
        endIndexOfTable={setEndIndex}
        requestForPaginationReset={paginationResetCount}
        showInLineLoader={pharmaStpFileState.showInlineLoader}
        onAddKeyButtonClick={handleAddKeyButtonClick}
        onSendToAppealBadgeClick={handleSendToAppealBadgeClick}
        onThreeDotsOptionClick={handleThreeDotsOptionClick}
      />
      {openedModalId === ModalId.SFTP_DOC_MODAL && (
        <SftpDocModal onClose={handleCloseSftpDocModal} />
      )}
      {openedModalId === ModalId.DOWNLOAD_FILES_MODAL && (
        <DownloadFilesModal onClose={() => dispatch(closeModal())} />
      )}
      {openedModalId === ModalId.TEXT_NOTE_DOC && (
        <TextNoteDoc
          onCancel={handleCloseTextNoteDocModal}
          onSave={handleSaveTextNoteDoc}
          filePath={screenshotUrl}
          loading={screenshotLoading}
          error={screenshotError}
          signedScreenshotUrl={signedScreenshotUrl}
          signedScreenshotLoading={signedScreenshotLoading}
          signedScreenshotError={signedScreenshotError}
          formattedTime={screenshotFormattedTime}
        />
      )}
      {openedModalId === ModalId.LETTER_UPLOAD_DOC && (
        <LetterUploadDoc
          onCancel={handleCloseLetterUploadDocModal}
          filePath={letterUploadScreenshotUrl}
          loading={letterUploadLoading}
          error={letterUploadError}
          formattedTime={letterUploadTimestamp}
        />
      )}
      {openedModalId === ModalId.SFTP_RERUN_MODAL && (
        <SftpRerun
          onClose={handleCloseSftpRerunModal}
          onSave={handleSaveSftpRerun}
          isRunning={isRerunning}
        />
      )}
      {openedModalId === ModalId.SFTP_PUSH_PENDING_RECORDS_MODAL && (
        <SftpPushPendingRecords />
      )}
      {openedModalId === ModalId.SFTP_SUMMARY_MODAL && (
        <SftpSummary onClose={handleCloseSftpSummaryModal} onSave={() => {}} />
      )}
      {openedModalId === ModalId.STAT_FILE_UPLOAD_MODAL && (
        <StatFileUploadModal onClose={() => dispatch(closeModal())} />
      )}
      {openedModalId === ModalId.REASSIGN_CASE_MODAL && (
        <ReassignCaseModal
          onClose={() => dispatch(closeModal())}
          onAssign={handleReassignCaseAssign}
        />
      )}
      {sendToAppealRow && (
        <SendToAppealModal
          rowData={sendToAppealRow}
          onClose={handleCloseSendToAppealModal}
          onSuccess={handleSendToAppealSuccess}
          mrnSource="patient_mrn"
        />
      )}
      {appealDetailsIdentifier && (
        <AppealDetailsModal
          key={appealDetailsIdentifier}
          identifier={appealDetailsIdentifier}
          onClose={handleCloseAppealDetailsModal}
        />
      )}
    </>
  );
};

export default PharmaStpFileTable;
